const SELECTED_ATTRIBUTES_DATASET = "selectedAttributes";
const VARIATIONS_FIELD_SELECTOR = '.variations_form select[name^="attribute_"]';
const RENDER_SOURCE_DATASET = "renderSource";
const SERVER_RENDER_SOURCE = "server";
const SWATCH_TERM_SELECTOR = ".yay-swatches-attribute-term";
const SWATCH_COLOR_SELECTOR = ".yay-swatches-color";
const SWATCH_BOUND_DATASET = "swatchesBound";
const SWATCH_RADIO_WRAPPER_SELECTOR = ".yay-swatches-radio-wrapper";
const SWATCH_DROPDOWN_CLASS = "yay-swatches-dropdown";
const SWATCH_DROPDOWN_SELECT = ".yay-swatches-dropdown-select";

const initializeTooltip = (root) => {
  if (typeof window.tippy !== "function") {
    return;
  }

  const elements = root.querySelectorAll(
    `${SWATCH_TERM_SELECTOR}[data-show-tooltip="yes"]`
  );

  if (!elements.length) {
    return;
  }

  elements.forEach((element) => {
    if (element._tippy) {
      return;
    }

    const label = element.dataset.labelText || element.dataset.tippyText || "";
    const content = element.dataset.tooltipImg
      ? `<div class="yay-swatches-tooltip-content" style="display:flex;flex-direction:column;align-items:center;gap:6px"><div class="yay-swatches-tooltip-image" style="line-height:0"><img src="${element.dataset.tooltipImg}" alt="${label}" /></div><div class="yay-swatches-tooltip-label" style="text-align:center;">${label}</div></div>`
      : `<div class="yay-swatches-tooltip-content" style="display:flex;flex-direction:column;align-items:center;"><div class="yay-swatches-tooltip-label" style="text-align:center;">${label}</div></div>`;

    window.tippy(element, {
      content,
      allowHTML: true,
      placement: "top",
      interactive: false,
      theme: "yay-swatches",
    });
  });
};

const ensureArray = (value) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn("Brandy Swatches: unable to parse variants", error);
  }

  return [];
};

const parseJSONValue = (value) => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const candidates = [value];

  if (value.includes("&quot;")) {
    candidates.push(value.replace(/&quot;/g, '"'));
  }

  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate);
    } catch (error) {
      // Continue trying other candidates.
    }
  }

  return null;
};

const ensureObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const isOptionEnabled = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return Boolean(value);
  }

  if (typeof value === "string") {
    return ["1", "true", "yes", "enable", "show", "on"].includes(
      value.toLowerCase()
    );
  }

  return false;
};

const toDatasetFlag = (value) => (isOptionEnabled(value) ? "yes" : "no");

const DEFAULT_SWATCH_STYLES = {
  borderActiveColor: "#181818",
  borderNormalColor: "#F5F5F5",
  swatchCustomSize: "36",
  swatchDesign: "design_3",
  swatchSize: "medium",
  swatchStyle: "circle",
  imagePosition: "center",
};

const DEFAULT_BUTTON_STYLES = {
  buttonActiveColor: "#181818",
  buttonHoverColor: "#ffffff",
  buttonNormalColor: "#ffffff",
  borderActiveColor: "#181818",
  borderHoverColor: "#181818",
  borderNormalColor: "#d4d4d4",
  borderRadius: "4px",
  borderWidth: "1px",
  buttonSize: "medium",
  textActiveColor: "#ffffff",
  textHoverColor: "#181818",
  textNormalColor: "#181818",
};

const PRODUCT_WRAPPER_SELECTORS = [
  ".wc-block-grid__product",
  ".wc-block-product",
  "li.product",
  ".product",
];

const PRODUCT_IMAGE_SELECTORS = [
  "img.yay-swatches-attachment-image",
  ".wp-block-woocommerce-product-image img",
  ".woocommerce-loop-product__link img",
  ".attachment-woocommerce_thumbnail",
  ".wp-post-image",
];

const normalizeTerm = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeAttributeKey = (key) =>
  typeof key === "string"
    ? key
        .replace(/^attribute_/, "")
        .replace(/^pa_/, "")
        .trim()
    : "";

const getProductWrapperNode = (form) => {
  if (!form) {
    return null;
  }

  for (const selector of PRODUCT_WRAPPER_SELECTORS) {
    const wrapper = form.closest(selector);

    if (wrapper) {
      return wrapper;
    }
  }

  const contextWrapper = form.closest("[data-wp-context]");

  if (contextWrapper) {
    return contextWrapper;
  }

  return form.closest(".wc-block-grid__products") || null;
};

const getProductImages = (form) => {
  const wrapper = getProductWrapperNode(form);

  if (!wrapper) {
    return [];
  }

  const images = [];
  const seen = new Set();

  PRODUCT_IMAGE_SELECTORS.forEach((selector) => {
    wrapper.querySelectorAll(selector).forEach((node) => {
      if (node instanceof HTMLImageElement && !seen.has(node)) {
        images.push(node);
        seen.add(node);
      }
    });
  });

  return images;
};

const cacheOriginalProductImage = (image) => {
  if (!image || image.dataset.swatchesOriginalSrcCached === "yes") {
    return;
  }

  const src = image.getAttribute("src") || image.currentSrc || "";
  const srcset = image.getAttribute("srcset");
  const sizes = image.getAttribute("sizes");
  const alt = image.getAttribute("alt");

  if (src) {
    image.dataset.swatchesOriginalSrc = src;
  }

  if (srcset) {
    image.dataset.swatchesOriginalSrcset = srcset;
  }

  if (sizes) {
    image.dataset.swatchesOriginalSizes = sizes;
  }

  if (alt) {
    image.dataset.swatchesOriginalAlt = alt;
  }

  if (image.dataset.imageId) {
    image.dataset.swatchesOriginalImageId = image.dataset.imageId;
  }

  image.dataset.swatchesOriginalSrcCached = "yes";
};

const cacheOriginalProductImages = (images) => {
  images.forEach(cacheOriginalProductImage);
};

const restoreProductImage = (image) => {
  if (!image || image.dataset.swatchesOriginalSrcCached !== "yes") {
    return;
  }

  image.classList.remove("stop-hover-effect");

  if (image.dataset.swatchesOriginalSrc) {
    image.setAttribute("src", image.dataset.swatchesOriginalSrc);
  }

  if ("swatchesOriginalSrcset" in image.dataset) {
    const originalSrcset = image.dataset.swatchesOriginalSrcset;

    if (originalSrcset) {
      image.setAttribute("srcset", originalSrcset);
    } else {
      image.removeAttribute("srcset");
    }
  }

  if ("swatchesOriginalSizes" in image.dataset) {
    const originalSizes = image.dataset.swatchesOriginalSizes;

    if (originalSizes) {
      image.setAttribute("sizes", originalSizes);
    } else {
      image.removeAttribute("sizes");
    }
  }

  if ("swatchesOriginalAlt" in image.dataset) {
    image.setAttribute("alt", image.dataset.swatchesOriginalAlt || "");
  }

  if ("swatchesOriginalImageId" in image.dataset) {
    image.dataset.imageId = image.dataset.swatchesOriginalImageId || "";
  }
};

const restoreProductImages = (images) => {
  images.forEach(restoreProductImage);
};

const applyVariationImageToImage = (image, variation) => {
  if (!image || !variation) {
    return false;
  }

  const variationImage = ensureObject(variation.image);
  const src =
    variationImage?.src ||
    variationImage?.url ||
    variationImage?.full_src ||
    "";

  if (!src) {
    return false;
  }

  image.classList.add("stop-hover-effect");

  image.setAttribute("src", src);

  if (variationImage.srcset) {
    image.setAttribute("srcset", variationImage.srcset);
  } else if ("swatchesOriginalSrcset" in image.dataset) {
    image.removeAttribute("srcset");
  }

  if (variationImage.sizes) {
    image.setAttribute("sizes", variationImage.sizes);
  } else if ("swatchesOriginalSizes" in image.dataset) {
    image.removeAttribute("sizes");
  }

  if (variationImage.alt) {
    image.setAttribute("alt", variationImage.alt);
  }

  if (variation.image_id) {
    image.dataset.imageId = String(variation.image_id);
  }

  return true;
};

const applyVariationImageToImages = (images, variation) => {
  let applied = false;

  images.forEach((image) => {
    const result = applyVariationImageToImage(image, variation);
    applied = applied || result;
  });

  return applied;
};

const dispatchProductImageChange = (form, detail = {}) => {
  if (
    !form ||
    typeof window === "undefined" ||
    typeof window.CustomEvent !== "function"
  ) {
    return;
  }

  const wrapper = getProductWrapperNode(form);

  if (!wrapper) {
    return;
  }

  const event = new CustomEvent("brandy:product-image-changed", {
    detail,
    bubbles: true,
  });

  wrapper.dispatchEvent(event);
};

const collectSelectedAttributes = (form) => {
  const selected = {
    raw: {},
    normalized: {},
    selectionCount: 0,
  };

  if (!form) {
    return selected;
  }

  const selects = form.querySelectorAll(VARIATIONS_FIELD_SELECTOR);

  selects.forEach((select) => {
    if (!(select instanceof HTMLSelectElement)) {
      return;
    }

    const name = select.getAttribute("name") || "";

    if (!name) {
      return;
    }

    const value = normalizeTerm(select.value);
    const normalizedKey = normalizeAttributeKey(name);

    selected.raw[name] = value;

    if (normalizedKey) {
      selected.normalized[normalizedKey] = value;
    }

    if (value) {
      selected.selectionCount += 1;
    }
  });

  return selected;
};

const getSelectedAttributeValue = (selected, key) => {
  if (!key) {
    return "";
  }

  if (Object.prototype.hasOwnProperty.call(selected.raw, key)) {
    return selected.raw[key];
  }

  const normalizedKey = normalizeAttributeKey(key);

  if (
    normalizedKey &&
    Object.prototype.hasOwnProperty.call(selected.normalized, normalizedKey)
  ) {
    return selected.normalized[normalizedKey];
  }

  return "";
};

const getVariationDataFromForm = (form) => {
  if (!form) {
    return [];
  }

  if (Array.isArray(form.__brandySwatchesVariations)) {
    return form.__brandySwatchesVariations;
  }

  const datasetValue =
    form.dataset?.productVariations ||
    form.getAttribute("data-product_variations");
  const parsed = parseJSONValue(datasetValue);

  if (Array.isArray(parsed)) {
    form.__brandySwatchesVariations = parsed;
    return parsed;
  }

  form.__brandySwatchesVariations = [];
  return form.__brandySwatchesVariations;
};

const findBestMatchingVariation = (form, selected) => {
  const variations = getVariationDataFromForm(form);

  if (!variations.length) {
    return null;
  }

  let bestMatch = null;
  let bestScore = -1;

  variations.forEach((variation) => {
    if (variation?.is_in_stock === false) {
      return;
    }

    const attributes =
      ensureObject(variation?.attributes) || ensureObject(variation?.attribute);
    const attributeEntries = Object.entries(attributes);

    let matchesSelection = true;
    let matchCount = 0;
    let specificity = 0;

    attributeEntries.forEach(([key, value]) => {
      if (!matchesSelection) {
        return;
      }

      const variationValue = normalizeTerm(value);
      const selectedValue = getSelectedAttributeValue(selected, key);

      if (variationValue) {
        specificity += 1;
      }

      if (!selectedValue) {
        return;
      }

      if (variationValue && variationValue !== selectedValue) {
        matchesSelection = false;
        return;
      }

      matchCount += 1;
    });

    if (!matchesSelection) {
      return;
    }

    const candidateScore = matchCount * 10 + specificity;

    if (candidateScore > bestScore) {
      bestMatch = variation;
      bestScore = candidateScore;
    }
  });

  return bestMatch;
};

const getAddToCartButton = (form) => {
  const wrapper = getProductWrapperNode(form);

  if (!wrapper) {
    return null;
  }

  return (
    wrapper.querySelector(
      ".wp-block-woocommerce-product-button .add_to_cart_button"
    ) || wrapper.querySelector(".add_to_cart_button")
  );
};

const getAddToCartButtonTextNode = (button) =>
  button?.querySelector(".add_to_cart_button__text") || button;

const storeAddToCartButtonDefaults = (button) => {
  if (!button || button.dataset.swatchesButtonDefaultsStored === "yes") {
    return;
  }

  const textNode = getAddToCartButtonTextNode(button);
  const defaultText = textNode ? textNode.textContent.trim() : "";

  if (defaultText) {
    button.dataset.swatchesDefaultText = defaultText;
  }

  const ariaLabel = button.getAttribute("aria-label");

  if (ariaLabel) {
    button.dataset.swatchesDefaultAriaLabel = ariaLabel;
  } else {
    button.dataset.swatchesDefaultAriaLabel = "";
  }

  if (button instanceof HTMLAnchorElement) {
    button.dataset.swatchesDefaultHref = button.getAttribute("href") || "";
  }

  button.dataset.swatchesButtonDefaultsStored = "yes";
};

const setAddToCartButtonText = (button, text) => {
  if (!button) {
    return;
  }

  // Check if there's an icon element that we need to preserve
  const iconElement = button.querySelector(
    ".brandy-product-button-icon-content"
  );

  // Try to find the text node
  let textNode = button.querySelector(".add_to_cart_button__text");

  if (!textNode) {
    // If no text node exists, we need to create one
    // But first, preserve the icon if it exists
    if (iconElement) {
      // Temporarily remove icon to preserve it
      iconElement.remove();
    }

    // Create the text span with the new text
    textNode = document.createElement("span");
    textNode.className = "add_to_cart_button__text";
    textNode.innerHTML = `<span>${text}</span>`;

    // Clear button content
    button.innerHTML = "";

    // Add icon first if it exists
    if (iconElement) {
      button.appendChild(iconElement);
    }

    // Add text node
    button.appendChild(textNode);
  } else {
    // Text node exists, just update its content
    // The icon is outside this node, so it will be preserved
    textNode.innerHTML = `<span>${text}</span>`;
  }
};

const removeAppliedAttributesFromButton = (button) => {
  if (!button) {
    return;
  }

  const appliedKeys = button.dataset.swatchesAttributeKeys;

  if (!appliedKeys) {
    return;
  }

  appliedKeys
    .split(",")
    .map((key) => key.trim())
    .filter(Boolean)
    .forEach((key) => {
      button.removeAttribute(`data-${key}`);
    });

  delete button.dataset.swatchesAttributeKeys;
};

const resetAddToCartButton = (button) => {
  if (!button) {
    return;
  }

  storeAddToCartButtonDefaults(button);
  removeAppliedAttributesFromButton(button);
  button.removeAttribute("data-variation_id");
  button.removeAttribute("data-variation");
  button.removeAttribute("aria-disabled");

  const fallbackText =
    button.dataset.swatchesDefaultText ||
    window.wp?.i18n?.__("Select options", "woocommerce") ||
    "Select options";

  setAddToCartButtonText(button, fallbackText);

  if (button.dataset.swatchesDefaultAriaLabel !== undefined) {
    const ariaLabel = button.dataset.swatchesDefaultAriaLabel;

    if (ariaLabel) {
      button.setAttribute("aria-label", ariaLabel);
    } else {
      button.removeAttribute("aria-label");
    }
  }

  if (button instanceof HTMLAnchorElement) {
    if (button.dataset.swatchesDefaultHref !== undefined) {
      button.setAttribute("href", button.dataset.swatchesDefaultHref);
    }
  }

  button.classList.remove(
    "ajax_add_to_cart",
    "disabled",
    "wc-variation-selection-needed",
    "wc-variation-is-unavailable",
    "loading",
    "added"
  );
  button.dataset.swatchesState = "default";
  button.dataset.swatchesReady = "no";
};

const setAddToCartButtonOutOfStock = (button) => {
  if (!button) {
    return;
  }

  storeAddToCartButtonDefaults(button);
  removeAppliedAttributesFromButton(button);

  const outOfStockText =
    window.wp?.i18n?.__("Out of stock", "woocommerce") || "Out of stock";

  setAddToCartButtonText(button, outOfStockText);
  button.setAttribute("aria-label", outOfStockText);
  button.classList.remove("ajax_add_to_cart");
  button.classList.add("disabled", "wc-variation-is-unavailable");
  button.setAttribute("aria-disabled", "true");
  button.removeAttribute("data-variation_id");
  button.dataset.swatchesState = "out-of-stock";
  button.dataset.swatchesReady = "no";
};

const applyVariationToAddToCartButton = (button, form, variation, selected) => {
  if (!button || !form || !variation) {
    return;
  }

  storeAddToCartButtonDefaults(button);
  removeAppliedAttributesFromButton(button);

  const addToCartText =
    window.wp?.i18n?.__("Add to cart", "woocommerce") || "Add to cart";

  setAddToCartButtonText(button, addToCartText);
  button.setAttribute("aria-label", addToCartText);
  button.classList.add("ajax_add_to_cart");
  button.classList.remove(
    "disabled",
    "wc-variation-selection-needed",
    "wc-variation-is-unavailable",
    "loading",
    "added"
  );
  button.removeAttribute("aria-disabled");

  const productId =
    form.getAttribute("data-product_id") || form.dataset?.product_id || "";

  if (productId) {
    button.setAttribute("data-product_id", productId);
  }

  const variationId = variation.variation_id || variation.variationId || "";

  if (variationId) {
    button.setAttribute("data-variation_id", variationId);
  }

  const quantityInput = form.querySelector('input[name="quantity"]');

  if (quantityInput && quantityInput.value) {
    button.setAttribute("data-quantity", quantityInput.value);
  }

  const appliedKeys = new Set();

  if (selected?.raw) {
    Object.entries(selected.raw).forEach(([key, value]) => {
      const normalizedValue = normalizeTerm(value);

      if (!normalizedValue) {
        return;
      }

      button.setAttribute(`data-${key}`, normalizedValue);
      appliedKeys.add(key);
    });
  }

  if (!appliedKeys.size) {
    const variationAttributes =
      ensureObject(variation.attributes) || ensureObject(variation.attribute);

    Object.entries(variationAttributes).forEach(([key, value]) => {
      const normalizedValue = normalizeTerm(value);

      if (!normalizedValue) {
        return;
      }

      let attributeKey = key;

      if (!attributeKey.startsWith("attribute_")) {
        const normalizedKey = normalizeAttributeKey(attributeKey);

        if (!normalizedKey) {
          return;
        }

        const prefixedKey = normalizedKey.startsWith("pa_")
          ? normalizedKey
          : `pa_${normalizedKey}`;

        attributeKey = `attribute_${prefixedKey}`;
      }

      button.setAttribute(`data-${attributeKey}`, normalizedValue);
      appliedKeys.add(attributeKey);
    });
  }

  if (appliedKeys.size) {
    button.dataset.swatchesAttributeKeys = Array.from(appliedKeys).join(",");
  } else {
    delete button.dataset.swatchesAttributeKeys;
  }

  button.dataset.swatchesState = "ready";
  button.dataset.swatchesReady = "yes";
};

const syncAddToCartButtonWithSelection = (form, selectedAttributes) => {
  if (!form) {
    return;
  }

  const button = getAddToCartButton(form);

  if (!button) {
    return;
  }

  storeAddToCartButtonDefaults(button);

  const selects = form.querySelectorAll(VARIATIONS_FIELD_SELECTOR);
  const totalAttributes = selects.length;

  if (!totalAttributes) {
    resetAddToCartButton(button);
    return;
  }

  const allSelected = Array.from(selects).every((select) =>
    normalizeTerm(select.value)
  );

  if (!allSelected) {
    resetAddToCartButton(button);
    return;
  }

  const selected = selectedAttributes || collectSelectedAttributes(form);
  const variation = findBestMatchingVariation(form, selected);

  if (!variation) {
    resetAddToCartButton(button);
    button.dataset.swatchesState = "no-match";
    return;
  }

  const purchasable =
    variation.is_purchasable === undefined ||
    isOptionEnabled(variation.is_purchasable);

  const active =
    variation.variation_is_active === undefined ||
    isOptionEnabled(variation.variation_is_active);

  const visible =
    variation.variation_is_visible === undefined ||
    isOptionEnabled(variation.variation_is_visible);

  const inStock =
    variation.is_in_stock === undefined ||
    isOptionEnabled(variation.is_in_stock) ||
    isOptionEnabled(variation.backorders_allowed) ||
    isOptionEnabled(variation.is_on_backorder);

  if (!purchasable || !active || !visible || !inStock) {
    setAddToCartButtonOutOfStock(button);
    return;
  }

  applyVariationToAddToCartButton(button, form, variation, selected);
};

const syncProductImageWithSelection = (form) => {
  if (!form) {
    return;
  }

  const selected = collectSelectedAttributes(form);

  syncAddToCartButtonWithSelection(form, selected);

  const images = getProductImages(form);

  if (!images.length) {
    return;
  }

  cacheOriginalProductImages(images);

  if (selected.selectionCount === 0) {
    restoreProductImages(images);
    return;
  }

  const variation = findBestMatchingVariation(form, selected);

  if (variation && applyVariationImageToImages(images, variation)) {
    dispatchProductImageChange(form, { variation });
    return;
  }

  restoreProductImages(images);
  dispatchProductImageChange(form, { variation: null, reset: true });
};

const overrideSelectionValue = (selection, attributeSlug, attributeValue) => {
  if (!selection) {
    return selection;
  }

  const normalizedAttribute = normalizeAttributeKey(attributeSlug);

  if (!normalizedAttribute) {
    return selection;
  }

  const normalizedValue = normalizeTerm(attributeValue);
  const previousValue = selection.normalized[normalizedAttribute] || "";

  ["attribute_", "pa_"].forEach((prefix) => {
    selection.raw[`${prefix}${normalizedAttribute}`] = normalizedValue;
  });

  selection.normalized[normalizedAttribute] = normalizedValue;

  if (previousValue && !normalizedValue) {
    selection.selectionCount = Math.max(selection.selectionCount - 1, 0);
  } else if (!previousValue && normalizedValue) {
    selection.selectionCount += 1;
  }

  return selection;
};

const getSwatchSize = (styles) => {
  switch (styles.swatchSize) {
    case "small":
      return "28px";
    case "medium":
      return "36px";
    case "large":
      return "44px";
    case "custom":
      return `${
        styles.swatchCustomSize || DEFAULT_SWATCH_STYLES.swatchCustomSize
      }px`;
    default:
      return "28px";
  }
};

const buildSwatchVariables = (styles) => {
  const merged = { ...DEFAULT_SWATCH_STYLES, ...ensureObject(styles) };
  const radius = merged.swatchStyle === "circle" ? "100%" : "4px";
  const size = getSwatchSize(merged);

  return [
    `--yay-swatches-swatch-border:${merged.borderNormalColor}`,
    `--yay-swatches-swatch-border-active:${merged.borderActiveColor}`,
    `--yay-swatches-swatch-border-radius:${radius}`,
    `--yay-swatches-swatch-size:${size}`,
  ].join(";");
};

const getButtonSizing = (size) => {
  switch (size) {
    case "small":
      return { fontSize: "12px", padding: "7px 12px" };
    case "large":
      return { fontSize: "16px", padding: "15px 25px" };
    default:
      return { fontSize: "14px", padding: "11px 16px" };
  }
};

const buildButtonVariables = (styles) => {
  const merged = { ...DEFAULT_BUTTON_STYLES, ...ensureObject(styles) };
  const { fontSize, padding } = getButtonSizing(merged.buttonSize);

  return [
    `--yay-swatches-button:${merged.buttonNormalColor}`,
    `--yay-swatches-button-hover:${merged.buttonHoverColor}`,
    `--yay-swatches-button-active:${merged.buttonActiveColor}`,
    `--yay-swatches-button-border:${merged.borderNormalColor}`,
    `--yay-swatches-button-border-active:${merged.borderActiveColor}`,
    `--yay-swatches-button-border-hover:${merged.borderHoverColor}`,
    `--yay-swatches-button-text:${merged.textNormalColor}`,
    `--yay-swatches-button-text-hover:${merged.textHoverColor}`,
    `--yay-swatches-button-text-active:${merged.textActiveColor}`,
    `--yay-swatches-button-border-radius:${merged.borderRadius}`,
    `--yay-swatches-button-border-width:${merged.borderWidth}`,
    `--yay-swatches-button-font-size:${fontSize}`,
    `--yay-swatches-button-padding:${padding}`,
  ].join(";");
};

const getSwatchDesignClass = (styles, attributeStyle) => {
  const merged = { ...DEFAULT_SWATCH_STYLES, ...ensureObject(styles) };

  switch (merged.swatchDesign) {
    case "design_1":
      return "yay-swatches-swatch--design-1";
    case "design_2":
      return "yay-swatches-swatch--design-2";
    case "design_3":
      return "yay-swatches-swatch--design-3";
    case "design_4":
      return "yay-swatches-swatch--design-4";
    case "design_5":
      return attributeStyle === "variant_image"
        ? "yay-swatches-swatch-variant-image"
        : "yay-swatches-swatch";
    case "design_6":
      return "yay-swatches-swatch--design-6";
    default:
      return "yay-swatches-swatch--design-3";
  }
};

const getSwatchTypeClass = (attributeStyle) => {
  switch (attributeStyle) {
    case "button":
      return "yay-swatches-button";
    case "variant_image":
      return "yay-swatches-swatch-variant-image";
    default:
      return "yay-swatches-swatch";
  }
};

const getVariationForm = (contextNode) =>
  contextNode?.querySelector(".variations_form");

const getAttributeSelect = (form, attributeSlug) => {
  if (!form || !attributeSlug) {
    return null;
  }

  const selectors = [
    `select[name="attribute_${attributeSlug}"]`,
    `select[name="attribute_pa_${attributeSlug}"]`,
  ];

  for (const selector of selectors) {
    const select = form.querySelector(selector);

    if (select) {
      return select;
    }
  }

  return null;
};

const findOriginalSwatchTerm = (form, attributeSlug, termValue) => {
  if (!form || !attributeSlug) {
    return null;
  }

  const normalizedAttribute = normalizeAttributeKey(attributeSlug);
  const normalizedTerm = normalizeTerm(termValue);

  if (!normalizedTerm) {
    return null;
  }

  const attributeCandidates = Array.from(
    new Set(
      [
        attributeSlug,
        normalizedAttribute,
        normalizedAttribute && `pa_${normalizedAttribute}`,
        normalizedAttribute && `attribute_${normalizedAttribute}`,
        normalizedAttribute && `attribute_pa_${normalizedAttribute}`,
      ].filter(Boolean)
    )
  );

  for (const candidate of attributeCandidates) {
    const selector = `.yay-variant-wrapper [data-attribute="${candidate}"][data-term="${normalizedTerm}"]`;
    const node = form.querySelector(selector);

    if (node) {
      return node;
    }
  }

  return null;
};

const triggerOriginalSwatch = (swatchNode) => {
  if (!swatchNode || typeof window === "undefined") {
    return false;
  }

  const event = new MouseEvent("click", {
    bubbles: true,
    cancelable: true,
    view: window,
  });

  swatchNode.dispatchEvent(event);
  return true;
};

const ensureSelectHasOption = (select, value, label) => {
  if (!select) {
    return;
  }

  const normalizedValue = normalizeTerm(value);

  if (!normalizedValue) {
    return;
  }

  const hasOption = Array.from(select.options).some(
    (option) => normalizeTerm(option.value) === normalizedValue
  );

  if (hasOption) {
    return;
  }

  const option = document.createElement("option");
  option.value = normalizedValue;
  option.textContent = label || normalizedValue;
  option.className = "attached enabled";
  option.dataset.swatchesDynamic = "yes";
  select.appendChild(option);
};

const applyVariationToForm = (form, variation) => {
  if (!form || !variation) {
    return;
  }

  const attributes =
    ensureObject(variation.attributes) || ensureObject(variation.attribute);

  if (!attributes || !Object.keys(attributes).length) {
    return;
  }

  form.dataset.swatchesAutoFilling = "yes";

  try {
    Object.entries(attributes).forEach(([key, value]) => {
      const normalizedKey = normalizeAttributeKey(key);
      const normalizedValue = normalizeTerm(value);

      if (!normalizedKey) {
        return;
      }

      const select = getAttributeSelect(form, normalizedKey);

      if (!select) {
        return;
      }

      ensureSelectHasOption(select, normalizedValue, normalizedValue);

      const currentValue = normalizeTerm(select.value);
      const hasChanged = currentValue !== normalizedValue;

      if (hasChanged) {
        select.value = normalizedValue;
      }

      triggerWooSelectEvents(select, hasChanged);
    });
  } finally {
    delete form.dataset.swatchesAutoFilling;
  }
};

const autoFillMissingAttributes = (form, sourceAttribute) => {
  if (
    !form ||
    form.dataset.swatchesAutoFilling === "yes" ||
    form.dataset.swatchesOriginalHandling === "yes" ||
    !getVariationDataFromForm(form).length
  ) {
    return;
  }

  const selected = collectSelectedAttributes(form);
  const variation = findBestMatchingVariation(form, selected);

  if (!variation) {
    return;
  }

  const attributes =
    ensureObject(variation.attributes) || ensureObject(variation.attribute);

  if (!attributes || !Object.keys(attributes).length) {
    return;
  }

  form.dataset.swatchesAutoFilling = "yes";

  try {
    Object.entries(attributes).forEach(([attributeKey, attributeValue]) => {
      const normalizedKey = normalizeAttributeKey(attributeKey);
      const normalizedValue = normalizeTerm(attributeValue);

      if (!normalizedKey || !normalizedValue) {
        return;
      }

      if (
        sourceAttribute &&
        normalizeAttributeKey(sourceAttribute) === normalizedKey
      ) {
        return;
      }

      const select = getAttributeSelect(form, normalizedKey);

      if (!select) {
        return;
      }

      const currentValue = normalizeTerm(select.value);

      if (
        currentValue === normalizedValue ||
        (sourceAttribute &&
          normalizeAttributeKey(sourceAttribute) === normalizedKey &&
          currentValue)
      ) {
        return;
      }

      const existingOption = Array.from(select.options).find(
        (option) => normalizeTerm(option.value) === normalizedValue
      );

      ensureSelectHasOption(
        select,
        normalizedValue,
        existingOption?.textContent || normalizedValue
      );

      updateWooSelectValue(select, normalizedValue);
    });
  } finally {
    delete form.dataset.swatchesAutoFilling;
  }
};

const triggerWooSelectEvents = (select, hasChanged) => {
  if (!select) {
    return;
  }

  select.dispatchEvent(new Event("change", { bubbles: true }));

  if (hasChanged) {
    select.dispatchEvent(new Event("input", { bubbles: true }));
  }
};

const updateWooSelectValue = (select, nextValue) => {
  if (!select) {
    return false;
  }

  const normalizedValue = typeof nextValue === "string" ? nextValue.trim() : "";
  const hasChanged = select.value !== normalizedValue;

  if (hasChanged) {
    select.value = normalizedValue;
  }

  triggerWooSelectEvents(select, hasChanged);

  return hasChanged;
};

const toggleTickState = (termNode, isActive) => {
  const wrapper = termNode?.parentElement;

  if (!wrapper) {
    return;
  }

  const shouldTick = wrapper.dataset.tickSelected === "enable";
  const colorNode = termNode.querySelector(SWATCH_COLOR_SELECTOR);

  if (!colorNode) {
    return;
  }

  if (shouldTick) {
    colorNode.classList.toggle("yay-swatches-tick-selected", isActive);

    if (isActive) {
      const computedStyle = colorNode.getAttribute("style") || "";
      const match = computedStyle.match(/background(?:-color)?:\s*([^;]+)/i);

      if (match && match[1]) {
        colorNode.style.setProperty("--yay-swatches-tick-color", match[1]);
      }
    }
  } else {
    colorNode.classList.remove("yay-swatches-tick-selected");
  }
};

const updateSwatchStateFromValue = (block, attributeSlug, termValue) => {
  if (!attributeSlug) {
    return;
  }

  const nodes = block.querySelectorAll(
    `${SWATCH_TERM_SELECTOR}[data-attribute="${attributeSlug}"]`
  );

  if (!nodes.length) {
    return;
  }

  const normalizedTerm = typeof termValue === "string" ? termValue.trim() : "";

  nodes.forEach((node) => {
    const isActive = normalizedTerm && node.dataset.term === normalizedTerm;
    node.classList.toggle("yay-swatches-active", isActive);
    toggleTickState(node, isActive);
  });

  const dropdownSelect = block.querySelector(
    `${SWATCH_DROPDOWN_SELECT}[data-attribute="${attributeSlug}"]`
  );

  if (dropdownSelect) {
    dropdownSelect.value = normalizedTerm;
  }
};

const syncAllSelectsWithSwatches = (block, form) => {
  if (!form) {
    return;
  }

  const selects = form.querySelectorAll(VARIATIONS_FIELD_SELECTOR);

  selects.forEach((select) => {
    const attributeSlug = getWooAttributeSlug(select.getAttribute("name"));

    if (!attributeSlug) {
      return;
    }

    updateSwatchStateFromValue(block, attributeSlug, select.value);
  });
};

const bindBlockToForm = (block) => {
  if (!block) {
    return;
  }

  const form = getVariationForm(block);

  const initialize = () => {
    if (form) {
      syncAllSelectsWithSwatches(block, form);
      syncProductImageWithSelection(form);
    }
  };

  if (!block.dataset[SWATCH_BOUND_DATASET]) {
    block.addEventListener("click", (event) => {
      let termNode = event.target.closest(SWATCH_TERM_SELECTOR);

      if (!termNode) {
        const radioWrapper = event.target.closest(
          SWATCH_RADIO_WRAPPER_SELECTOR
        );

        if (radioWrapper) {
          termNode = radioWrapper.querySelector(SWATCH_TERM_SELECTOR);
        }
      }

      if (!termNode || !block.contains(termNode)) {
        return;
      }

      const attributeSlug = termNode.dataset.attribute;
      const termValue = termNode.dataset.term;
      const isRadioInput =
        termNode.tagName === "INPUT" &&
        termNode.getAttribute("type") === "radio";
      const select = getAttributeSelect(form, attributeSlug);
      const originalSwatch = findOriginalSwatchTerm(
        form,
        attributeSlug,
        termValue
      );

      if (originalSwatch && !originalSwatch.isSameNode(termNode)) {
        if (form) {
          form.dataset.swatchesOriginalHandling = "yes";
        }

        const delegateSelect =
          select || getAttributeSelect(form, attributeSlug);

        if (delegateSelect) {
          ensureSelectHasOption(
            delegateSelect,
            termValue,
            termNode.dataset.labelText
          );
        }

        const delegated = triggerOriginalSwatch(originalSwatch);

        if (delegated) {
          if (!isRadioInput) {
            event.preventDefault();
          }

          const syncSelectValue = () => {
            const targetSelect =
              select || getAttributeSelect(form, attributeSlug);

            if (targetSelect) {
              ensureSelectHasOption(
                targetSelect,
                termValue,
                termNode.dataset.labelText
              );
              updateWooSelectValue(targetSelect, termValue);
            }
          };

          if (form && typeof window !== "undefined") {
            window.setTimeout(() => {
              delete form.dataset.swatchesOriginalHandling;
              syncSelectValue();
              autoFillMissingAttributes(form, attributeSlug);
              syncAllSelectsWithSwatches(block, form);
              syncProductImageWithSelection(form);
            }, 0);
          } else if (form) {
            delete form.dataset.swatchesOriginalHandling;
            syncSelectValue();
            autoFillMissingAttributes(form, attributeSlug);
            syncAllSelectsWithSwatches(block, form);
            syncProductImageWithSelection(form);
          }

          return;
        }

        if (form) {
          delete form.dataset.swatchesOriginalHandling;
        }
      }

      if (!select) {
        updateSwatchStateFromValue(block, attributeSlug, termValue);
        return;
      }

      if (termNode.classList.contains(SWATCH_DROPDOWN_CLASS)) {
        event.preventDefault();

        updateWooSelectValue(select, termValue);
        updateSwatchStateFromValue(block, attributeSlug, select.value);
        syncProductImageWithSelection(form);
        return;
      }

      if (!isRadioInput) {
        event.preventDefault();
      }

      const selection = overrideSelectionValue(
        collectSelectedAttributes(form),
        attributeSlug,
        termValue
      );
      const variation = findBestMatchingVariation(form, selection);

      if (variation) {
        applyVariationToForm(form, variation);
        return;
      }

      ensureSelectHasOption(select, termValue, termNode.dataset.labelText);

      const optionExists = Array.from(select.options).some(
        (option) => option.value === termValue
      );

      if (!optionExists) {
        return;
      }

      updateWooSelectValue(select, termValue);
      syncProductImageWithSelection(form);

      if (isRadioInput && !termNode.checked) {
        termNode.checked = true;
      }
    });

    if (form) {
      form.addEventListener("change", (event) => {
        const { target } = event;

        if (!(target instanceof HTMLSelectElement)) {
          return;
        }

        const attributeSlug = getWooAttributeSlug(target.getAttribute("name"));

        if (!attributeSlug) {
          return;
        }

        updateSwatchStateFromValue(block, attributeSlug, target.value);
        if (
          !form.dataset.swatchesAutoFilling &&
          form.dataset.swatchesOriginalHandling !== "yes"
        ) {
          autoFillMissingAttributes(form, attributeSlug);
        }
        syncProductImageWithSelection(form);
      });

      const resetButton = form.querySelector(".reset_variations");

      if (resetButton) {
        resetButton.addEventListener("click", () => {
          window.setTimeout(() => {
            syncAllSelectsWithSwatches(block, form);
            syncProductImageWithSelection(form);
          }, 0);
        });
      }
    }

    block.querySelectorAll(SWATCH_DROPDOWN_SELECT).forEach((customSelect) => {
      const attributeSlug = customSelect.dataset.attribute;

      if (!attributeSlug) {
        return;
      }

      customSelect.addEventListener("change", (event) => {
        const select = getAttributeSelect(form, attributeSlug);

        if (!select) {
          return;
        }

        const value = event.target.value;

        ensureSelectHasOption(
          select,
          value,
          event.target.selectedOptions?.[0]?.text
        );
        updateWooSelectValue(select, value);
        updateSwatchStateFromValue(block, attributeSlug, select.value);
        syncProductImageWithSelection(form);
      });
    });

    block.dataset[SWATCH_BOUND_DATASET] = "yes";
  }

  initialize();
  initializeTooltip(block);
};

const getBackgroundPosition = (styles) => {
  switch (styles.imagePosition) {
    case "top":
      return { position: "center top", size: "cover" };
    case "bottom":
      return { position: "center bottom", size: "cover" };
    case "center":
      return { position: "center center", size: "contain" };
    default:
      return { position: "center center", size: "cover" };
  }
};

const buildSwatchColorStyle = (styles, option) => {
  const merged = { ...DEFAULT_SWATCH_STYLES, ...ensureObject(styles) };

  if (option.image) {
    const { position, size } = getBackgroundPosition(merged);
    return [
      `background:url(${option.image})`,
      `background-position:${position}`,
      "background-repeat:no-repeat",
      "background-color:transparent",
      `background-size:${size}`,
    ].join(";");
  }

  if (option.showDual && option.dualColor && option.color) {
    const radius = merged.swatchStyle === "circle" ? "50%" : "4px";
    return [
      `border-radius:${radius}`,
      `background:linear-gradient(135deg, ${option.color} 50%, ${option.dualColor} 50%)`,
    ].join(";");
  }

  if (option.color) {
    return `background:${option.color};`;
  }

  return "";
};

const createSwatchNode = (attributeMeta, option, globalStyles = {}) => {
  const term = document.createElement("span");
  term.className = "yay-swatches-attribute-term yay-swatches-custom";
  term.dataset.attribute = attributeMeta.value;
  term.dataset.term = option.slug;
  term.dataset.type = attributeMeta.style || "custom";
  term.dataset.labelText = option.name;
  term.dataset.tippyText = option.name;

  const attributeStyle = attributeMeta.style || "custom";
  const swatchStyles = ensureObject(globalStyles.swatch);
  const buttonStyles = ensureObject(globalStyles.button);

  if (attributeStyle === "button") {
    term.className = "yay-swatches-attribute-term yay-swatches-button";
    term.style.cssText = buildButtonVariables(buttonStyles);
    term.textContent = option.name;
    return term;
  }

  const typeClass = getSwatchTypeClass(attributeStyle);
  const designClass = getSwatchDesignClass(swatchStyles, attributeStyle);

  term.className = ["yay-swatches-attribute-term", typeClass, designClass]
    .filter(Boolean)
    .join(" ");

  const swatchVariables = buildSwatchVariables(swatchStyles);

  if (swatchVariables) {
    term.style.cssText = swatchVariables;
  }

  if (option.image) {
    term.dataset.tooltipImg = option.image;
  }

  const color = document.createElement("span");
  color.className = "yay-swatches-color";
  const colorStyle = buildSwatchColorStyle(swatchStyles, option);

  if (colorStyle) {
    color.style.cssText = colorStyle;
  } else if (option.color) {
    color.style.backgroundColor = option.color;
  }

  term.appendChild(color);

  return term;
};

const getWooAttributeSlug = (fieldName) => {
  if (!fieldName) {
    return "";
  }

  return fieldName
    .replace(/^attribute_/, "")
    .replace(/^pa_/, "")
    .trim();
};

const collectAllowedAttributes = (contextNode) => {
  const form = getVariationForm(contextNode);

  if (!form) {
    return new Set();
  }

  const selects = form.querySelectorAll(VARIATIONS_FIELD_SELECTOR);
  const allowed = new Set();

  selects.forEach((select) => {
    const slug = getWooAttributeSlug(select.getAttribute("name"));

    if (slug) {
      allowed.add(slug);
    }
  });

  return allowed;
};

const collectAllowedTerms = (contextNode) => {
  const form = getVariationForm(contextNode);

  if (!form) {
    return new Map();
  }

  const allowed = new Map();
  const datasetValue =
    form.dataset?.productVariations ||
    form.getAttribute("data-product_variations");
  const variationData = parseJSONValue(datasetValue);

  if (Array.isArray(variationData)) {
    variationData.forEach((variation) => {
      const attributes = variation?.attributes || variation?.attribute || {};

      Object.entries(attributes).forEach(([key, termSlug]) => {
        const attributeSlug = getWooAttributeSlug(key);
        const normalizedTerm =
          typeof termSlug === "string" ? termSlug.trim() : "";

        if (!attributeSlug || !normalizedTerm) {
          return;
        }

        const terms = allowed.get(attributeSlug) || new Set();
        terms.add(normalizedTerm);
        allowed.set(attributeSlug, terms);
      });
    });
  }

  if (allowed.size) {
    return allowed;
  }

  const selects = form.querySelectorAll(VARIATIONS_FIELD_SELECTOR);

  selects.forEach((select) => {
    const attributeSlug = getWooAttributeSlug(select.getAttribute("name"));

    if (!attributeSlug) {
      return;
    }

    const terms = allowed.get(attributeSlug) || new Set();

    Array.from(select.options).forEach((option) => {
      const value = typeof option.value === "string" ? option.value.trim() : "";

      if (value) {
        terms.add(value);
      }
    });

    if (terms.size) {
      allowed.set(attributeSlug, terms);
    }
  });

  return allowed;
};

const renderBlock = (block, brandyData) => {
  const {
    variants = [],
    variantsOptions = [],
    styles: globalStyles = {},
  } = brandyData;
  const selectedAttributes = ensureArray(
    block.dataset[SELECTED_ATTRIBUTES_DATASET]
  );
  const allowedAttributes = collectAllowedAttributes(block);
  const allowedTerms = collectAllowedTerms(block);

  const hasVariationData =
    allowedAttributes.size > 0 ||
    Array.from(allowedTerms.values()).some((terms) => terms.size > 0);

  block.innerHTML = "";

  if (!hasVariationData) {
    block.style.display = "none";
    return;
  }

  block.style.removeProperty("display");

  if (!selectedAttributes.length) {
    const defaults = variants.map(({ value }) => value);

    if (!defaults.length) {
      return;
    }

    selectedAttributes.push(...defaults);
  }

  selectedAttributes.forEach((attributeName) => {
    const attributeMeta = variants.find(({ value }) => value === attributeName);

    if (!attributeMeta) {
      return;
    }

    // Skip attributes that are not part of the current product variation form.
    if (allowedAttributes.size && !allowedAttributes.has(attributeMeta.value)) {
      return;
    }

    const groupWrapper = document.createElement("div");
    groupWrapper.className = "brandy-block-swatches__group";

    const label = document.createElement("p");
    label.className =
      "brandy-block-swatches__label yay-swatches-attribute-name";
    label.textContent = attributeMeta.label || attributeMeta.value;
    groupWrapper.appendChild(label);

    const wrapper = document.createElement("div");
    wrapper.className = "yay-variant-wrapper";
    wrapper.dataset.attribute = attributeMeta.value;
    wrapper.dataset.type = attributeMeta.style || "custom";
    const swatchStyles = ensureObject(globalStyles.swatch);
    wrapper.dataset.showTooltip = toDatasetFlag(swatchStyles.swatchTooltip);
    wrapper.dataset.showImage = toDatasetFlag(swatchStyles.swatchTooltipImage);
    wrapper.dataset.arrow = toDatasetFlag(swatchStyles.swatchTooltipArrow);
    wrapper.dataset.shadow = toDatasetFlag(swatchStyles.swatchTooltipBoxShadow);
    wrapper.dataset.animation = toDatasetFlag(
      swatchStyles.swatchTooltipAnimation
    );
    const clearButtonHidden =
      swatchStyles.clearButtonShowHideOptions === "hide" &&
      swatchStyles.hideClearButtonOptions !== "shop";
    wrapper.dataset.clearButton = clearButtonHidden ? "disable" : "enable";
    wrapper.dataset.tickSelected = isOptionEnabled(swatchStyles.tickSelected)
      ? "enable"
      : "disable";

    variantsOptions
      .filter((option) => option.attribute === attributeMeta.value)
      .filter((option) => {
        const terms = allowedTerms.get(attributeMeta.value);

        if (!terms || !terms.size) {
          return true;
        }

        return terms.has(option.slug);
      })
      .forEach((option) => {
        wrapper.appendChild(
          createSwatchNode(attributeMeta, option, globalStyles)
        );
      });

    if (!wrapper.children.length) {
      return;
    }

    groupWrapper.appendChild(wrapper);
    block.appendChild(groupWrapper);
  });

  if (!block.children.length) {
    const emptyMessage = document.createElement("p");
    emptyMessage.className = "brandy-block-swatches__empty";
    emptyMessage.textContent =
      window.wp?.i18n?.__("No swatches available.", "brandy-blocks") ||
      "No swatches available.";
    block.appendChild(emptyMessage);
  } else {
    bindBlockToForm(block);
  }
};

const init = () => {
  const brandyData = window.brandySwatchesBlock;

  if (!brandyData) {
    return;
  }

  const blocks = document.querySelectorAll(".brandy-block-swatches");

  blocks.forEach((block) => {
    if (block.dataset[RENDER_SOURCE_DATASET] === SERVER_RENDER_SOURCE) {
      bindBlockToForm(block);
      return;
    }

    renderBlock(block, brandyData);
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
